import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/index.js';
import { Icon } from '../../components/Icons.jsx';
import { notify } from '@/lib/notifications';
import { PlatformIcon } from './shared/PlatformIcon.jsx';
import { PLATFORM_LABELS } from './shared/platformLabels.js';
import { api } from '../../services/api/client.js';
import { CreditConfirmationModal } from '../../components/CreditConfirmationModal.jsx';
import { useUpgrade } from '../../contexts/UpgradeContext.jsx';
import { useDashboardData } from '../../state/DashboardDataContext.jsx';
import CreationAccessNotice, { creationAccessState } from '../../components/billing/CreationAccessNotice.jsx';

export function StrategyOverviewTab({ strategy, setStrategy, onJumpTo }) {
  const { t } = useTranslation();
  const dash = useDashboardData();
  const creationAllowed = creationAccessState(dash) === 'allowed';
  const { openUpgrade } = useUpgrade();
  const [confirming, setConfirming] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const inner = strategy?.strategy ?? null;
  const summary = inner?.summary || strategy?.summary;
  const positioning = inner?.positioning || strategy?.positioning;
  const insights = inner?.targetAudienceInsights || [];
  const pillars = inner?.contentPillars || (strategy?.contentPillars || []).map((name) => ({ name, purpose: t('strategyOverviewTab.pillarPurposeBuildTrust') }));
  const recommendedTone = inner?.recommendedTone;
  const successMetrics = inner?.successMetrics || [];
  const platforms = strategy?.recommendedPlatforms || Object.keys(inner?.platformStrategy || {});
  const formats = strategy?.recommendedFormats || [];
  const platformStrategy = inner?.platformStrategy || {};
  const platformInstructions = inner?.platformInstructions || {};
  const quickSuggestionCount = Array.isArray(inner?.quickSuggestions) ? inner.quickSuggestions.length : 0;
  const strategicBullets = buildStrategicBullets(t, { positioning, pillars, recommendedTone, successMetrics, platforms, firstPost: inner?.firstPostRecommendation });

  const hasStrategy = !!strategy;

  async function generate(confirmCreditSpend, instruction = '') {
    if (!creationAllowed) return;
    const trimmedInstruction = instruction.trim();
    try {
      setGenerating(true);
      setGenerationError(null);
      const result = await api.strategy.generate({
        confirmCreditSpend,
        ...(trimmedInstruction ? { instruction: trimmedInstruction } : {})
      });
      setStrategy(result.strategy);
      notify('contentPlan.generated');
      setConfirming(false);
    } catch (err) {
      if (err?.code === 'PLAN_LAPSED') {
        dash.invalidateResource?.('entitlements', { fresh: true });
        openUpgrade({ feature: 'default', variant: 'renew' });
      }
      const msg = err.message || 'Content Plan generation failed';
      const errorCopy = contentPlanGenerationErrorCopy(err);
      setGenerationError(errorCopy);
      if (err?.code === 'CREDIT_REQUIRED' || err?.code === 'QUOTA_EXCEEDED' || err?.details?.reason === 'CREDIT_CONFIRMATION_REQUIRED' || /credit|quota/i.test(msg)) {
        setConfirming(true);
        notify('contentPlan.creditRequired', { description: errorCopy });
      } else {
        notify('contentPlan.generationFailed', { description: errorCopy });
      }
    } finally {
      setGenerating(false);
    }
  }

  if (!hasStrategy) {
    return (
      <div className="card ds-card-pad settings-card">
        <CreationAccessNotice />
        <div className="settings-card-header">
          <h3 className="settings-card-title">{t('strategyOverviewTab.noPlanTitle')}</h3>
          <p className="settings-card-description">
            {t('strategyOverviewTab.noPlanDescription')}
          </p>
        </div>
        <div className="settings-card-footer" style={{ borderTop: 0, paddingTop: 0, marginTop: 0 }}>
          <span />
          <button className="btn btn-primary" onClick={() => generate(false)} disabled={generating || !creationAllowed}>
            <Icon name="sparkles" size={13} /> {generating ? t('strategyOverviewTab.generating') : t('strategyOverviewTab.generatePlan')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <CreationAccessNotice />
      <div className="cp-module-shell">
        <div className="cp-module-hero cp-plan-hero">
          <div>
            <div className="settings-section-label">{t('strategyOverviewTab.planActiveLabel')}</div>
            <h3>{t('strategyOverviewTab.planActiveTitle')}</h3>
            <p>{summary || t('strategyOverviewTab.planActiveSummary')}</p>
          </div>
          <div className="cp-plan-version">
            <span>{t('strategyOverviewTab.version')}</span>
            <strong>{strategy?.version ?? 1}</strong>
          </div>
        </div>

        <div className="cp-plan-card-grid">
          <PlanCard title={t('strategyOverviewTab.cardIdeasTitle')} body={quickSuggestionCount ? t('strategyOverviewTab.cardIdeasBody', { count: quickSuggestionCount }) : t('strategyOverviewTab.cardIdeasBodyEmpty')} icon="sparkles" />
          <PlanCard title={t('strategyOverviewTab.cardChannelStyleTitle')} body={platforms.length ? t('strategyOverviewTab.cardChannelStyleBody', { list: platforms.slice(0, 3).map(platformLabel).join(', '), more: platforms.length > 3 ? t('strategyOverviewTab.andMore') : '' }) : t('strategyOverviewTab.cardChannelStyleBodyEmpty')} icon="layers" />
          <PlanCard title={t('strategyOverviewTab.cardPostLengthTitle')} body={firstContentLength(platformInstructions, platformStrategy) || t('strategyOverviewTab.cardPostLengthBodyEmpty')} icon="edit" />
          <PlanCard title={t('strategyOverviewTab.cardCtaTitle')} body={inner?.firstPostRecommendation?.cta || t('strategyOverviewTab.cardCtaBodyEmpty')} icon="target" />
          <PlanCard title={t('strategyOverviewTab.cardReferencesTitle')} body={t('strategyOverviewTab.cardReferencesBody')} icon="image" />
        </div>

        {strategicBullets.length > 0 && (
          <section className="cp-module-panel is-open">
            <div className="cp-module-body" style={{ paddingTop: 0 }}>
              <div className="settings-section-label" style={{ marginBottom: 8 }}>{t('strategyOverviewTab.simpleStrategy')}</div>
              <ul className="cp-bullet-list">
                {strategicBullets.map((line, i) => <li key={i}>{line}</li>)}
              </ul>
            </div>
          </section>
        )}

        <section className="cp-module-panel is-open">
          <button type="button" className="cp-module-trigger" onClick={() => setDetailsOpen((v) => !v)} aria-expanded={detailsOpen}>
            <span className="cp-node-dot is-ready" />
            <span className="cp-module-title-block">
              <strong>{t('strategyOverviewTab.whatAiIsUsing')}</strong>
              <span>{t('strategyOverviewTab.whatAiIsUsingSub')}</span>
            </span>
            <span className="cp-node-status">{t('strategyOverviewTab.ready')}</span>
            <Icon name={detailsOpen ? 'chev_u' : 'chev_d'} size={14} />
          </button>
          {detailsOpen && (
            <div className="cp-module-body">
              {insights.length > 0 && <Section title={t('strategyOverviewTab.sectionAudienceInsights')}><ul className="cp-bullet-list">{insights.map((line, i) => <li key={i}>{line}</li>)}</ul></Section>}
              {pillars.length > 0 && <Section title={t('strategyOverviewTab.sectionThemes')}><div className="cp-chip-row">{pillars.slice(0, 6).map((pillar, i) => <span key={`${pillar.name}-${i}`} className="chip chip-violet" title={pillar.purpose || ''}>{pillar.name}</span>)}</div></Section>}
              {platforms.length > 0 && <Section title={t('strategyOverviewTab.sectionChannels')}><div className="cp-chip-row">{platforms.map((id) => <span key={id} className="chip cp-platform-chip"><PlatformIcon platform={id} size={12} withColor={false} />{PLATFORM_LABELS[id] || id}</span>)}</div></Section>}
              {formats.length > 0 && <Section title={t('strategyOverviewTab.sectionFormats')}><div className="cp-chip-row">{formats.map((f, i) => <span key={i} className="chip">{String(f).replaceAll('_', ' ')}</span>)}</div></Section>}
              {recommendedTone && <Section title={t('strategyOverviewTab.sectionVoice')}><p className="body" style={{ margin: 0 }}>{recommendedTone}</p></Section>}
            </div>
          )}
        </section>

        <div className="cp-sticky-savebar">
          <button className="btn btn-secondary" onClick={() => { setGenerationError(null); setConfirming(true); }} disabled={generating || !creationAllowed}>
            <Icon name="sparkles" size={12} /> {t('strategyOverviewTab.regenerate')}
          </button>
          <button className="btn btn-ghost" onClick={() => onJumpTo?.('brand')}>
            {t('strategyOverviewTab.adjustInputs')} <Icon name="arrow_r" size={12} />
          </button>
          <button className="btn btn-ghost" onClick={() => onJumpTo?.('rules')}>
            {t('strategyOverviewTab.publishingRules')} <Icon name="arrow_r" size={12} />
          </button>
        </div>
      </div>

      {confirming && creationAllowed && (
        <ConfirmRegenerateModal
          generating={generating}
          error={generationError}
          onCancel={() => { setConfirming(false); setGenerationError(null); }}
          onConfirm={(instruction) => generate(true, instruction)}
        />
      )}
    </>
  );
}

function PlanCard({ title, body, icon, children }) {
  return (
    <article className="cp-plan-card">
      <span className="cp-section-icon" aria-hidden="true"><Icon name={icon} size={15} /></span>
      <div>
        <div className="settings-section-label">{title}</div>
        {body && <p>{body}</p>}
        {children}
      </div>
    </article>
  );
}

function buildStrategicBullets(t, { positioning, pillars, recommendedTone, successMetrics, platforms, firstPost }) {
  const bullets = [];
  if (positioning) bullets.push(t('strategyOverviewTab.bulletPositioning', { positioning }));
  if (Array.isArray(pillars) && pillars.length) bullets.push(t('strategyOverviewTab.bulletPillars', { pillars: pillars.slice(0, 3).map((pillar) => pillar.name).filter(Boolean).join(', ') }));
  if (recommendedTone) bullets.push(t('strategyOverviewTab.bulletTone', { tone: recommendedTone }));
  if (firstPost?.topic) bullets.push(t('strategyOverviewTab.bulletFirstPost', { topic: firstPost.topic }));
  if (Array.isArray(platforms) && platforms.length) bullets.push(t('strategyOverviewTab.bulletPlatforms', { list: platforms.slice(0, 3).map(platformLabel).join(', '), more: platforms.length > 3 ? t('strategyOverviewTab.andMore') : '' }));
  if (Array.isArray(successMetrics) && successMetrics.length) bullets.push(t('strategyOverviewTab.bulletMetrics', { metrics: successMetrics.slice(0, 2).join(t('strategyOverviewTab.metricsAnd')) }));
  return bullets.filter(Boolean).slice(0, 5);
}

function firstContentLength(platformInstructions, platformStrategy) {
  const sources = [...Object.values(platformInstructions || {}), ...Object.values(platformStrategy || {})];
  const match = sources.find((item) => item?.contentLength);
  return match?.contentLength || null;
}

function platformLabel(platform) {
  return PLATFORM_LABELS[platform] || platform;
}

export function contentPlanGenerationErrorCopy(error) {
  const reason = error?.details?.reason;
  const traceId = error?.details?.traceId;
  const aiCode = error?.details?.aiCode;
  let message = '';
  if (error?.code === 'PLAN_LAPSED') {
    message = i18n.t('paymentIssue.lapsedBody');
  } else if (reason === 'CREDIT_CONFIRMATION_REQUIRED' || reason === 'INSUFFICIENT_CREDITS' || error?.code === 'QUOTA_EXCEEDED' || /credit|quota/i.test(error?.message || '')) {
    message = i18n.t('strategyOverviewTab.errorQuota');
  } else if (error?.code === 'INTERNAL_ERROR' && /setup|configured|api key/i.test(error?.message || '')) {
    message = i18n.t('strategyOverviewTab.errorSetup');
  } else if (aiCode === 'AI_PROVIDER_MALFORMED_JSON' || aiCode === 'AI_PROVIDER_INVALID_RESPONSE') {
    message = i18n.t('strategyOverviewTab.errorInvalidResponse');
  } else if (error?.status === 0) {
    message = i18n.t('strategyOverviewTab.errorConnection');
  } else if (aiCode === 'AI_PROVIDER_NETWORK_ERROR' || aiCode === 'AI_PROVIDER_TIMEOUT' || error?.code === 'NETWORK_ERROR') {
    message = i18n.t('strategyOverviewTab.errorTimeout');
  } else if (error?.status === 401 || error?.status === 403) {
    message = i18n.t('strategyOverviewTab.errorSession');
  } else if (error?.status === 429 || error?.code === 'RATE_LIMITED') {
    message = i18n.t('strategyOverviewTab.errorBusy');
  } else {
    message = i18n.t('strategyOverviewTab.errorGeneric');
  }
  return traceId ? i18n.t('strategyOverviewTab.withTraceId', { message, traceId }) : message;
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 18 }}>
      <div className="settings-section-label" style={{ marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}

export function ConfirmRegenerateModal({ generating, error, onCancel, onConfirm, creditCost = 1 }) {
  const { t } = useTranslation();
  const { openUpgrade } = useUpgrade();
  const [instruction, setInstruction] = useState('');

  return <CreditConfirmationModal
    open
    title={t('strategyOverviewTab.modalTitle')}
    description={t('strategyOverviewTab.modalDescription')}
    creditCost={creditCost}
    confirming={generating}
    confirmLabel={t('strategyOverviewTab.modalConfirmLabel')}
    confirmingLabel={t('strategyOverviewTab.modalConfirmingLabel')}
    onCancel={onCancel}
    onConfirm={() => onConfirm?.(instruction)}
    onBuyCredits={() => openUpgrade({ feature: 'quota' })}
    onUpgrade={() => openUpgrade({ feature: 'quota' })}
  >
    <div style={{ marginTop: 14 }}>
      <label className="label" htmlFor="regenerate-plan-instruction">{t('strategyOverviewTab.modalInstructionLabel')}</label>
      <textarea
        id="regenerate-plan-instruction"
        className="textarea"
        value={instruction}
        onChange={(event) => setInstruction(event.target.value)}
        placeholder={t('strategyOverviewTab.modalInstructionPlaceholder')}
        disabled={generating}
      />
      <div className="helper">{t('strategyOverviewTab.modalInstructionHelper')}</div>
      {error && <div className="auth-error" role="alert" style={{ marginTop: 12 }}><Icon name="alert" size={13} /> {error}</div>}
    </div>
  </CreditConfirmationModal>;
}
